/**
 * Voces PREBUILT de Google Gemini TTS (`gemini-3.8-flash-lite-tts`).
 *
 * Mismo criterio que `realtime-voices.ts`: una sola fuente para el backend
 * (validación de la voz de sesión / REST y del `module_config`) y el frontend
 * (selector de voz de speak). Agregar/quitar una voz = tocar SOLO este array.
 *
 * El `id` es el nombre que la API espera en
 * `generation_config.speech_config[].voice`. `descriptor` es la "persona" que
 * Google publica para cada voz (en inglés, tal cual la doc). No hay dato de
 * género en la fuente — no lo inventamos.
 *
 * Una voz de Gemini se distingue de una de ElevenLabs por pertenecer a este
 * catálogo cerrado (los ids de ElevenLabs son opacos de 20 caracteres).
 *
 * Dos familias, ambas referenciadas IGUAL en `speech_config[].voice` (el id
 * tal cual, sin prefijo `voices/`):
 *   · las 30 prebuilt de siempre (`GEMINI_TTS_VOICES`, nombres de estrella,
 *     todas en-US General American);
 *   · un ALLOWLIST explícito de voces de la Extended Voice Library
 *     (`GEMINI_TTS_LIBRARY_VOICES`, ids `en-gb-tutor-4`…) para acentos que
 *     no son en-US. La librería tiene ~2000 voces; sólo entran las curadas
 *     acá — un id con el mismo patrón que no esté en la lista se RECHAZA
 *     (la API no devuelve error HTTP ante un id inexistente: el stream cierra
 *     sin audio, así que la validación tiene que ser nuestra).
 */
export const TTS_PROVIDERS = ['elevenlabs', 'gemini'] as const;
export type TtsProviderId = (typeof TTS_PROVIDERS)[number];

export function isTtsProviderId(value: unknown): value is TtsProviderId {
  return typeof value === 'string' && (TTS_PROVIDERS as readonly string[]).includes(value);
}

export const GEMINI_TTS_VOICES = [
  { id: 'Zephyr', descriptor: 'Bright' },
  { id: 'Puck', descriptor: 'Upbeat' },
  { id: 'Charon', descriptor: 'Informative' },
  { id: 'Kore', descriptor: 'Firm' },
  { id: 'Fenrir', descriptor: 'Excitable' },
  { id: 'Leda', descriptor: 'Youthful' },
  { id: 'Orus', descriptor: 'Firm' },
  { id: 'Aoede', descriptor: 'Breezy' },
  { id: 'Callirrhoe', descriptor: 'Easy-going' },
  { id: 'Autonoe', descriptor: 'Bright' },
  { id: 'Enceladus', descriptor: 'Breathy' },
  { id: 'Iapetus', descriptor: 'Clear' },
  { id: 'Umbriel', descriptor: 'Easy-going' },
  { id: 'Algieba', descriptor: 'Smooth' },
  { id: 'Despina', descriptor: 'Smooth' },
  { id: 'Erinome', descriptor: 'Clear' },
  { id: 'Algenib', descriptor: 'Gravelly' },
  { id: 'Rasalgethi', descriptor: 'Informative' },
  { id: 'Laomedeia', descriptor: 'Upbeat' },
  { id: 'Achernar', descriptor: 'Soft' },
  { id: 'Alnilam', descriptor: 'Firm' },
  { id: 'Schedar', descriptor: 'Even' },
  { id: 'Gacrux', descriptor: 'Mature' },
  { id: 'Pulcherrima', descriptor: 'Forward' },
  { id: 'Achird', descriptor: 'Friendly' },
  { id: 'Zubenelgenubi', descriptor: 'Casual' },
  { id: 'Vindemiatrix', descriptor: 'Gentle' },
  { id: 'Sadachbia', descriptor: 'Lively' },
  { id: 'Sadaltager', descriptor: 'Knowledgeable' },
  { id: 'Sulafat', descriptor: 'Warm' },
] as const;

/**
 * Voces de la Extended Voice Library de Gemini habilitadas en orborix.
 * Metadata copiada TAL CUAL de Google (`GET v1beta/voices`, 2026-09-24):
 * `languageCode`, `accent`, `gender` y `descriptor` (su `description`,
 * recortada a la parte de la voz). Elegidas para el picker de speak por
 * acento (británico / australiano) con perfil docente/cercano; verificadas
 * sintetizando con `gemini-3.8-flash-lite-tts` (mismo TTFA que las prebuilt).
 * Agregar una = verificar el id contra la API + sumar su fila acá.
 */
export const GEMINI_TTS_LIBRARY_VOICES = [
  {
    id: 'en-gb-tutor-4',
    languageCode: 'en-GB',
    accent: 'Winchester English',
    gender: 'female',
    descriptor: 'Teacher — textured, resonant, and soothing',
  },
  {
    id: 'en-gb-assistant-2',
    languageCode: 'en-GB',
    accent: 'Winchester English',
    gender: 'male',
    descriptor: 'Friend — professional yet approachable',
  },
  {
    id: 'en-au-tutor-3',
    languageCode: 'en-AU',
    accent: 'Sydney English',
    gender: 'female',
    descriptor: 'Teacher — resonant and witty',
  },
  {
    id: 'en-au-tutor-5',
    languageCode: 'en-AU',
    accent: 'Sydney English',
    gender: 'male',
    descriptor: 'Professor — conversational, slightly dry humor, and warm',
  },
] as const;

export type GeminiTtsPrebuiltVoiceId = (typeof GEMINI_TTS_VOICES)[number]['id'];
export type GeminiTtsLibraryVoiceId = (typeof GEMINI_TTS_LIBRARY_VOICES)[number]['id'];
export type GeminiTtsVoiceId = GeminiTtsPrebuiltVoiceId | GeminiTtsLibraryVoiceId;

/** Todas las voces válidas para Gemini: prebuilt + allowlist de la librería. */
export const GEMINI_TTS_VOICE_IDS: readonly GeminiTtsVoiceId[] = [
  ...GEMINI_TTS_VOICES.map((v) => v.id),
  ...GEMINI_TTS_LIBRARY_VOICES.map((v) => v.id),
];

export function isGeminiTtsVoice(value: unknown): value is GeminiTtsVoiceId {
  return typeof value === 'string' && (GEMINI_TTS_VOICE_IDS as readonly string[]).includes(value);
}

/**
 * Forma de un id de la Extended Voice Library (`<lang>-<region>-<rol>-<n>`,
 * ej. `en-gb-tutor-4`). Sirve para RECHAZAR ids de la librería que no están
 * en el allowlist: si no, por tener ≥8 chars pasarían como id de ElevenLabs.
 */
const GEMINI_LIBRARY_ID_RE = /^[a-z]{2,3}-[a-z0-9]{2,3}-[a-z]+-\d+$/;

export function looksLikeGeminiLibraryVoiceId(value: string): boolean {
  return GEMINI_LIBRARY_ID_RE.test(value);
}

/**
 * ¿`voiceId` es una voz válida para `provider`? Gemini = pertenece al catálogo
 * de arriba (prebuilt o allowlist de la librería); ElevenLabs = cualquier id NO
 * vacío que NO sea una voz de Gemini ni tenga forma de id de la librería
 * (el catálogo de ElevenLabs es abierto: voces premade, clonadas, etc.).
 */
export function isVoiceForTtsProvider(provider: TtsProviderId, voiceId: string): boolean {
  if (provider === 'gemini') return isGeminiTtsVoice(voiceId);
  return voiceId.length > 0 && !isGeminiTtsVoice(voiceId) && !looksLikeGeminiLibraryVoiceId(voiceId);
}
